import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { ToastrService } from 'ngx-toastr';

import { MsalService } from '@azure/msal-angular';

import { Client } from '@microsoft/microsoft-graph-client';

import { Observable } from 'rxjs/Observable';

import { OAuthSettings } from './msal.config';

import { AuthService } from '../auth.service';

import { environment } from '../../../../environments/environment';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { of } from 'rxjs/observable/of';

@Injectable()
export class AuthMSALService {

  constructor(private readonly msalService: MsalService,
              private readonly toastrService: ToastrService,
              private readonly router: Router,
              private readonly httpClient: HttpClient,
              private readonly authService: AuthService) {
  }

  checkLoginOnRedirect(notifyError: boolean = true) {
    if (!this.authService.isAuthenticated()) {
      this
        .fetchUserToken()
        .subscribe(
          response => {
            localStorage.setItem('userToken', response.userToken);
            const decodedUser = this.authService.decodeUserFromToken(response.userToken).user;
            this.authService.setCurrentUserFromId(decodedUser);
          },
          error => {
            if (notifyError) {
              this.toastrService.error('Access denied');
              console.log(error);
            }
          }
        );
    } else {
      this.authService.initialiseLogin();
    }
  }

  signIn(): Observable<any> {
    this.authService.clearUser();

    // MOCK: avoid to call Azure and directly fetch the token
    // NB: the `MsalInterceptor` into the `app.module.ts` needs to be commented out
    if (environment.useMockserver) {
      this.checkLoginOnRedirect(false);
      return of(null);
    }

    return fromPromise(this.msalService.loginPopup(OAuthSettings.scopes));
  }

  signOut(): void {
    this.msalService.logout();
  }

  fetchUserToken(): Observable<any> {
    return this.httpClient.post<any>('/api/login/token', {});
  }

  getAccessToken(scopes, tokenName = 'userToken'): Promise<string> {
    return this.msalService.acquireTokenSilent(scopes)
      .then(token => {
        localStorage.setItem(tokenName, token);
        return token;
      }).catch(error => {
        console.log('Failed to get token!', error);

        return this.msalService.acquireTokenPopup(scopes)
          .then(token => {
            localStorage.setItem(tokenName, token);
            return Promise.resolve(token);
          }).catch(innerError => {
            console.log('Failed to get token (from popup)!', innerError);
            return Promise.resolve('');
          });
      });
  }

  async inviteUser(invitedUserDisplayName: string, invitedUserEmailAddress: string) {
    // get an instance of the graph client
    const graphClient = this.getGraphClient();

    // Get the user from Graph (GET /me)
    const invite = await graphClient.api('/invitations').post({
      'invitedUserDisplayName': invitedUserDisplayName,
      'invitedUserEmailAddress': invitedUserEmailAddress,
      'inviteRedirectUrl': 'https://demo.sapphirecrm.uk',
      'sendInvitationMessage': 'true'
    }).then(() => {
      this.toastrService.success('Invite successfully sent.');
    }).catch(error => {
      this.toastrService.error('Invite sending failed.');
      console.log('invite sending failed', error);
    });
  }

  async getGuestUsers() {
    const graphClient = this.getGraphClient();
    const guestUsers = await graphClient.api(`/users?$filter=UserType eq 'Guest'`).version('beta').get();

    return guestUsers[ 'value' ];
  }

  getGraphClient(): Client {
    const graphClient = Client.init({
      authProvider: async (done) => {
        const token = await this
          .getAccessToken(OAuthSettings.graphScopes, 'graphToken')
          .catch((reason) => {
            done(reason, null);
          });

        if (token) {
          done(null, token);
        } else {
          done('Could not get an access token', null);
        }
      }
    });

    return graphClient;
  }
}



// WEBPACK FOOTER //
// ./client/app/core/auth/msal/auth-msal.service.ts